import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import api from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../ui/card";
import { Video, Heart, Coins, Share2 } from "lucide-react";

// Platform → svg/path tuples (no extra dep needed)
const PLATFORMS = [
  { id: "x", label: "X", color: "#1da1f2" },
  { id: "facebook", label: "Facebook", color: "#1877f2" },
  { id: "linkedin", label: "LinkedIn", color: "#0a66c2" },
];

/**
 * Producer Pulse — 7-day mini-sparkline trio for videos / applauds / credits.
 * Inline SVG, no chart lib. Fetches /api/me/pulse on mount; gracefully shows
 * dashes if anything fails. Pure dopamine widget — no business actions here.
 */
const Sparkline = ({ data, color, width = 96, height = 28 }) => {
  const path = useMemo(() => {
    if (!data || data.length === 0) return "";
    const max = Math.max(1, ...data);
    const stepX = data.length > 1 ? width / (data.length - 1) : 0;
    return data.map((v, i) => {
      const x = i * stepX;
      const y = height - (v / max) * (height - 2) - 1;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
  }, [data, width, height]);

  // Dot for the latest point — gives the chart a finishing accent.
  const last = data && data.length ? data[data.length - 1] : 0;
  const max = Math.max(1, ...(data || [1]));
  const lastY = height - (last / max) * (height - 2) - 1;
  const lastX = data && data.length > 1 ? width : 0;

  return (
    <svg width={width} height={height} className="overflow-visible">
      <path d={path} fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
      {data && data.length > 0 && (
        <circle cx={lastX} cy={lastY} r="2.5" fill={color} />
      )}
    </svg>
  );
};

const PulseCell = ({ icon: Icon, label, total, data, color, suffix = "" }) => (
  <div className="flex items-center gap-3 flex-1 min-w-0">
    <div className={`shrink-0 rounded-lg p-2 border`} style={{ borderColor: `${color}40`, background: `${color}14` }}>
      <Icon className="h-4 w-4" style={{ color }} />
    </div>
    <div className="min-w-0">
      <p className="text-gray-400 text-[10px] uppercase tracking-wide">{label}</p>
      <p className="text-white font-semibold leading-tight">{total}{suffix}</p>
    </div>
    <div className="ml-auto">
      <Sparkline data={data} color={color} />
    </div>
  </div>
);

// Build the share intent text — keeps the producer's pulse stats in the post body
// so the screenshot/preview tells the story even before the click.
const buildShareText = (user, pulse) => {
  const profileUrl = `${window.location.origin}/${user.username}`;
  const v = pulse.totals?.videos || 0;
  const a = pulse.totals?.applauds || 0;
  const c = (pulse.totals?.credits || 0).toFixed(1);
  const text = `My GlobalEyes.TV pulse this week: ${v} video${v === 1 ? "" : "s"} · ${a} applaud${a === 1 ? "" : "s"} · ${c} credits earned. Catch me at ${profileUrl}`;
  return { text, profileUrl };
};

const SHARE_INTENTS = {
  x: ({ text }) =>
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&hashtags=GlobalEyes,MadeByHumans&via=GlobalEyesDOTtv`,
  facebook: ({ profileUrl, text }) =>
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(profileUrl)}&quote=${encodeURIComponent(text)}`,
  linkedin: ({ profileUrl }) =>
    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(profileUrl)}`,
};

const ProducerPulse = () => {
  const { token, user, refreshUser } = useAuth();
  const [pulse, setPulse] = useState(null);
  const [sharing, setSharing] = useState(null); // platform id while in flight

  useEffect(() => {
    if (!token) return;
    let stop = false;
    api.get(`/me/pulse?days=7`)
      .then(r => { if (!stop) setPulse(r.data); })
      .catch(() => { /* silent — widget hides */ });
    return () => { stop = true; };
  }, [token]);

  const handleShare = async (platformId) => {
    if (!user || !pulse) return;
    const { text, profileUrl } = buildShareText(user, pulse);
    const intentBuilder = SHARE_INTENTS[platformId];
    if (!intentBuilder) return;

    // Open the share intent FIRST so we don't lose the popup-blocker user-gesture.
    const url = intentBuilder({ text, profileUrl });
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=600");

    // Claim the credit reward (idempotent per platform per ISO week)
    setSharing(platformId);
    try {
      const res = await api.post(`/me/pulse/share`, { platform: platformId });
      if (res.data?.rewarded) {
        toast.success(res.data.message || `+${(res.data.reward_credits || 0).toFixed(2)} credits`);
        refreshUser?.();
      } else {
        // Already rewarded this week — don't shout, but acknowledge
        toast.info(res.data?.message || "Share recorded.");
      }
    } catch (err) {
      // Don't block the share UX — log silently
      console.error("pulse share reward failed", err);
    }
    setSharing(null);
  };

  if (!user || !pulse) return null;
  const totalActivity = (pulse.totals?.videos || 0) + (pulse.totals?.applauds || 0) + (pulse.totals?.credits || 0);
  if (totalActivity === 0) return null;  // hide empty pulse — don't shout zeros

  return (
    <Card className="bg-gradient-to-r from-gray-900 via-gray-900 to-emerald-950/30 border-gray-800 mb-6" data-testid="producer-pulse">
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-emerald-300 text-xs sm:text-sm font-medium tracking-wide">YOUR PULSE · LAST 7 DAYS</p>
          <p className="text-gray-500 text-[10px] hidden sm:block">{(pulse.labels || []).join(' · ')}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
          <PulseCell icon={Video} label="Videos posted" total={pulse.totals.videos}
                     data={pulse.videos} color="#10b981" />
          <PulseCell icon={Heart} label="Applauds in" total={pulse.totals.applauds}
                     data={pulse.applauds} color="#ef4444" />
          <PulseCell icon={Coins} label="Credits earned" total={pulse.totals.credits.toFixed(1)}
                     data={pulse.credits} color="#f59e0b" />
        </div>
        {/* Share row — viral growth loop, +1 credit per platform per ISO week */}
        <div className="mt-3 pt-3 border-t border-gray-800/70 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-1.5 text-gray-400 text-[11px]">
            <Share2 className="h-3.5 w-3.5" />
            <span>Brag about your week — earn +1 credit per platform/week</span>
          </div>
          <div className="flex items-center gap-2 sm:ml-auto">
            {PLATFORMS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => handleShare(p.id)}
                disabled={sharing === p.id}
                data-testid={`pulse-share-${p.id}`}
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors disabled:opacity-50"
                style={{
                  color: p.color,
                  borderColor: `${p.color}55`,
                  background: `${p.color}11`,
                }}
                title={`Share to ${p.label}`}
              >
                {sharing === p.id ? "…" : p.label}
              </button>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProducerPulse;
